(() => {
  if (customElements.get('edvi-header')) return;
  class EdviHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('[data-edvi-menu]');
      this.trigger = this.querySelector('[data-edvi-menu-open]');
      this.panels = [...this.querySelectorAll('[data-edvi-panel]')];
      this.stack = [this.panels[0].id];
      this.returnButtons = [];
      this.trigger.addEventListener('click', () => this.openMenu(), options);
      this.querySelector('[data-edvi-menu-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.closeMenu(), options);
      this.dialog.addEventListener('pointerdown', event => {
        const bounds = this.dialog.getBoundingClientRect();
        this.backdropDown = event.clientX < bounds.left || event.clientX > bounds.right;
      }, options);
      this.dialog.addEventListener('click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (this.backdropDown && (event.clientX < bounds.left || event.clientX > bounds.right)) this.dialog.close();
        this.backdropDown = false;
        const forward = event.target.closest('[data-edvi-panel-open]');
        if (forward) {
          this.stack.push(forward.dataset.edviPanelOpen);
          this.returnButtons.push(forward);
          this.updatePanels();
          this.activePanel().querySelector('button,a[href]')?.focus({ preventScroll: true });
        }
        if (event.target.closest('[data-edvi-panel-back]') && this.stack.length > 1) {
          this.stack.pop();
          const previous = this.returnButtons.pop();
          this.updatePanels();
          previous?.focus({ preventScroll: true });
        }
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const targets = [...this.dialog.querySelectorAll('a[href],button:not([disabled]),select,input')]
          .filter(element => !element.closest('[inert]') && element.getClientRects().length);
        const first = targets[0], last = targets.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, options);
      this.querySelectorAll('[data-edvi-dropdown]').forEach(details => {
        details.addEventListener('mouseenter', () => {
          if (!matchMedia('(hover: hover)').matches) return;
          for (const sibling of this.querySelectorAll('[data-edvi-dropdown][open]')) {
            if (sibling !== details && !sibling.contains(details)) sibling.open = false;
          }
          details.open = true;
        }, options);
        details.addEventListener('mouseleave', () => { if (!details.contains(document.activeElement)) details.open = false; }, options);
        details.addEventListener('focusout', () => {
          requestAnimationFrame(() => { if (!details.contains(document.activeElement) && !details.matches(':hover')) details.open = false; });
        }, options);
      });
      document.addEventListener('pointerdown', event => {
        this.querySelectorAll('[data-edvi-dropdown][open],[data-edvi-localization][open]').forEach(details => {
          if (!details.contains(event.target)) details.open = false;
        });
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.dialog.open) return;
        const details = event.target.closest('details[open]');
        if (details) { details.open = false; details.querySelector('summary')?.focus(); event.preventDefault(); }
      }, options);
      this.desktop = matchMedia('(min-width: 1200px)');
      this.desktop.addEventListener('change', () => {
        if (this.desktop.matches && this.dialog.open) this.dialog.close();
        this.updateHeaderHeight();
      }, options);
      this.headerSection = this.closest('.edvi-header-section');
      if (this.headerSection) {
        this.headerResize = new ResizeObserver(() => this.updateHeaderHeight());
        this.headerResize.observe(this.headerSection, { box: 'border-box' });
        this.updateHeaderHeight();
      }
      const categories = this.querySelector('[data-edvi-categories]');
      if (categories) {
        const updateEdges = () => {
          const left = Math.abs(categories.scrollLeft);
          categories.parentElement.classList.toggle('has-left', left > 1);
          categories.parentElement.classList.toggle('at-end', left + categories.clientWidth >= categories.scrollWidth - 1);
        };
        categories.addEventListener('scroll', updateEdges, { ...options, passive: true });
        this.resize = new ResizeObserver(updateEdges);
        this.resize.observe(categories);
        updateEdges();
      }
      this.updatePanels();
    }
    disconnectedCallback() {
      if (this.dialog?.open) { this.dialog.close(); this.closeMenu(); }
      this.abort?.abort();
      this.resize?.disconnect();
      this.headerResize?.disconnect();
      if (!document.querySelector('edvi-header')) document.documentElement.style.removeProperty('--edvi-header-height');
    }
    updateHeaderHeight() {
      if (!this.isConnected || !this.headerSection) return;
      const position = getComputedStyle(this.headerSection).position;
      const height = position === 'sticky' || position === 'fixed' ? this.headerSection.getBoundingClientRect().height : 0;
      const value = `${Math.ceil(height)}px`;
      const rootStyle = document.documentElement.style;
      if (rootStyle.getPropertyValue('--edvi-header-height') !== value) rootStyle.setProperty('--edvi-header-height', value);
    }
    activePanel() { return this.panels.find(panel => panel.id === this.stack.at(-1)); }
    openMenu() {
      this.stack = [this.panels[0].id];
      this.returnButtons = [];
      this.updatePanels();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.dialog.querySelector('[data-edvi-menu-close]').focus({ preventScroll: true });
    }
    closeMenu() {
      document.body.style.overflow = this.previousOverflow || '';
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.isConnected && !this.desktop.matches) this.trigger.focus({ preventScroll: true });
    }
    updatePanels() {
      const current = this.stack.at(-1);
      this.panels.forEach(panel => {
        const active = panel.id === current;
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-before', !active && this.stack.includes(panel.id));
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.querySelectorAll('[data-edvi-panel-open]').forEach(button => {
        button.setAttribute('aria-expanded', String(this.stack.includes(button.dataset.edviPanelOpen)));
      });
    }
  }
  customElements.define('edvi-header', EdviHeader);
})();
